import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import SafeImage from '../../components/SafeImage';
import ProjectCard from '../../components/ProjectCard';
import { clientService } from '../../services/clientService';
import { projectService } from '../../services/projectService';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const ClientDetail = () => {
  const { slug } = useParams();
  const [client, setClient] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useDocumentTitle(client?.name || "Our Clients");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchAsync = async () => {
    try {
      setError("");
      setLoading(true);
      setClient(null);
      setRelatedProjects([]);

      const response = await clientService.getBySlugAsync(slug);
      if (!response.isSuccess) {
        setError(response.errors?.[0] || "Client not found.");
        return;
      }
      setClient(response.value);

      const projectsResponse = await projectService.getByClientAsync(response.value.id);
      if (projectsResponse.isSuccess) {
        setRelatedProjects(projectsResponse.value || []);
      }
    } catch (err) {
      setError("Unable to load this client right now. Please try again later.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const gallery = client?.images || [];

  return (
    <>
      <PageTitle title={client?.name || "Our Clients"} />

      <section className="blog-details pt-120 pb-120">
        <div className="container">
          {loading && (
            <div className="row">
              <div className="col-12 text-center">
                <p>Loading client...</p>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="row">
              <div className="col-12 text-center">
                <p>{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && client && (
            <>
              <div className="row">
                <div className="col-xl-8 col-lg-7">
                  <div className="blog-details__left">
                    {client.mainImagePath && (
                      <div className="blog-details__img">
                        <SafeImage src={client.mainImagePath} alt={client.name} style={{ width: "100%" }} />
                      </div>
                    )}

                    <div className="blog-details__content">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div
                          style={{
                            width: 70,
                            height: 70,
                            background: "#ffffff",
                            border: "1px solid #eef0ef",
                            borderRadius: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 8,
                            flexShrink: 0,
                          }}
                        >
                          <SafeImage
                            src={client.logoPath}
                            alt={client.name}
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        </div>
                        {client.websiteUrl && (
                          <a href={client.websiteUrl} target="_blank" rel="noopener noreferrer">
                            <i className="fas fa-arrow-up-right-from-square"></i> Visit Website
                          </a>
                        )}
                      </div>
                      <h3 className="blog-details__title">{client.name}</h3>
                      {client.shortDescription && <p className="blog-details__text-2">{client.shortDescription}</p>}
                      {client.fullDescription && (
                        <p className="blog-details__text-2" style={{ whiteSpace: "pre-line" }}>{client.fullDescription}</p>
                      )}
                    </div>

                    {gallery.length > 0 && (
                      <div className="mt-5">
                        <div className="sec-title mb-4">
                          <h6 className="sub-title">Photo Gallery</h6>
                          <h2 className="title">Gallery</h2>
                        </div>
                        <div className="row g-3">
                          {gallery.map((image) => (
                            <div className="col-md-4 col-6" key={image.id}>
                              <SafeImage
                                src={image.imagePath}
                                alt={client.name}
                                style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
                                onClick={() => setLightboxSrc(image.imagePath)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-xl-4 col-lg-5">
                  <div className="sidebar">
                    {client.websiteUrl && (
                      <div className="sidebar__single">
                        <a
                          href={client.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-two-rounded w-100 text-center"
                        >
                          Visit Website <i className="fa-regular fa-arrow-up-right" />
                        </a>
                      </div>
                    )}
                    <div className="sidebar__single">
                      <div className="card border-0 shadow-sm p-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Related Projects</span>
                          <span className="fw-bold">{relatedProjects.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {relatedProjects.length > 0 && (
                <div className="row mt-5">
                  <div className="col-12">
                    <div className="sec-title center mb-4">
                      <h6 className="sub-title">Case Studies</h6>
                      <h2 className="title">Related Projects</h2>
                    </div>
                  </div>
                  {relatedProjects.map((project, index) => (
                    <ProjectCard key={project.id} project={project} animationDelay={`${(index % 3) * 100}ms`} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.85)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 30,
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 32,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={lightboxSrc}
            alt="Gallery enlarged"
            style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
          />
        </div>
      )}
    </>
  );
};

export default ClientDetail;
